import { useCallback, useEffect, useState } from 'react'
import { adminApi } from '@/services/api'
import { configApi } from '@/services/api/config'

type DirPermState = 'aenderbar' | 'sichtbar' | 'versteckt'

// v3/F7: Rollen-Filter macht >50 Direktoren pro Schule praktisch ausgeschlossen;
// bei Bedarf hier anheben (Endpoint-Ceiling liegt bei 50).
const DIRECTORS_FETCH_LIMIT = 50

interface DirectorPermissionsEditorProps {
  /** Feld-/Karten-Key (z.B. 'absence_types' oder ein Registry-Key) */
  fieldKey: string
  fieldLabel: string
}

/**
 * Direktorkonfig v2 (D12/D16/E7): Superadmin steuert pro Direktor den
 * Zustand eines Feldes/einer Karte: änderbar (Default, kein Eintrag) /
 * sichtbar / versteckt. Nur Restriktionen werden gespeichert —
 * «änderbar» löscht die Zeile (kein Eintrag = volle Rechte, D13).
 */
export function DirectorPermissionsEditor({ fieldKey, fieldLabel }: DirectorPermissionsEditorProps) {
  const [directors, setDirectors] = useState<Array<{ user_id: string; email: string; first_name?: string | null; last_name?: string | null }>>([])
  const [directorsLoading, setDirectorsLoading] = useState(true)
  const [selected, setSelected] = useState<string | null>(null)
  const [state, setState] = useState<DirPermState | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    setDirectorsLoading(true)
    adminApi.searchUsers({ limit: DIRECTORS_FETCH_LIMIT, role: 'director' })
      .then(res => {
        const list = res.users ?? []
        setDirectors(list)
        // Reparatur-Fix C8: Stale-Auswahl vermeiden — vorherige Auswahl nur
        // behalten, wenn die Person noch in der (ggf. gefilterten) Liste ist.
        setSelected(prev =>
          prev && list.some(d => d.user_id === prev) ? prev : (list[0]?.user_id ?? null))
      })
      .catch(() => setError('Direktoren konnten nicht geladen werden'))
      .finally(() => setDirectorsLoading(false))
  }, [])

  const load = useCallback(async (directorId: string) => {
    setLoading(true)
    setError(null)
    setNotice(null)
    try {
      const res = await configApi.getDirectorPermissions(directorId)
      const p = res.permissions?.[fieldKey]
      setState(!p ? 'aenderbar' : p.editable ? 'aenderbar' : p.visible ? 'sichtbar' : 'versteckt')
    } catch {
      setError('Rechte konnten nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }, [fieldKey])

  useEffect(() => { if (selected) load(selected) }, [selected, load])

  const save = async (next: DirPermState) => {
    if (!selected) return
    setSaving(true)
    setError(null)
    setNotice(null)
    try {
      await configApi.updateDirectorPermissions(selected, {
        [fieldKey]: {
          visible: next !== 'versteckt',
          editable: next === 'aenderbar',
        },
      })
      setState(next)
      setNotice('Gespeichert.')
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        ?? 'Speichern fehlgeschlagen'
      setError(String(msg))
    } finally {
      setSaving(false)
    }
  }

  if (directors.length === 0) {
    // Reparatur-Fix C8: Fetch-Fehler werden nicht mehr falsch als «Keine
    // Direktoren vorhanden» diagnostiziert; Ladezustand ist sichtbar.
    return (
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-1">Direktor-Rechte: {fieldLabel}</h3>
        {directorsLoading ? (
          <p className="text-sm text-gray-500">Direktoren werden geladen…</p>
        ) : error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : (
          <p className="text-sm text-gray-500">Keine Direktoren vorhanden — erstellen Sie zuerst einen Direktor-Benutzer.</p>
        )}
      </div>
    )
  }

  const name = (d: { email: string; first_name?: string | null; last_name?: string | null }) =>
    [d.first_name, d.last_name].filter(Boolean).join(' ') || d.email

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-900 mb-1">Direktor-Rechte: {fieldLabel}</h3>
      <p className="text-xs text-gray-500 mb-3">
        Standardmässig dürfen alle Direktoren alles ändern (D13). Hier können Sie das Recht
        dieser Karte pro Person entziehen — änderbar / nur sichtbar / versteckt.
      </p>
      <div className="flex items-center gap-3">
        <select
          value={selected ?? ''}
          onChange={e => {
            // Beim Wechsel den 3-State zurücksetzen — der alte Zustand des
            // vorherigen Direktors wird sonst bis zum Load-Ende angezeigt.
            setSelected(e.target.value)
            setState(null)
          }}
          disabled={loading}
          className="rounded-md border border-gray-300 shadow-sm px-3 py-2 text-sm"
        >
          {directors.map(d => (
            <option key={d.user_id} value={d.user_id}>{name(d)}</option>
          ))}
        </select>
        <div className="flex items-center gap-0.5 p-0.5 bg-gray-100 rounded-lg">
          {([['aenderbar', '✏️ Änderbar'], ['sichtbar', '👁 Sichtbar'], ['versteckt', '🔒 Versteckt']] as const).map(([id, label]) => (
            <button
              key={id}
              onClick={() => save(id)}
              disabled={loading || saving}
              title={`«${label}» für diesen Direktor setzen`}
              className={`px-2.5 py-1 text-xs rounded-md transition ${
                state === id ? 'bg-white shadow-sm font-semibold text-gray-800' : 'text-gray-500 hover:text-gray-700'
              } ${loading || saving ? 'opacity-60 pointer-events-none' : ''}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
      {notice && <p className="text-xs text-green-700 mt-2">{notice}</p>}
    </div>
  )
}
